import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { TeamOutlined, RightOutlined, UserOutlined } from "@ant-design/icons";
import { Spin, Pagination } from "antd";

import {
  selectAdminStateUsers,
  selectIsAdminStateLoading,
  selectTotalElements,
} from "../../../redux-toolkit/admin/admin-selector";
import { fetchAllUsers, fetchAllDistUsers } from "../../../redux-toolkit/admin/admin-thunks";
import { resetAdminState } from "../../../redux-toolkit/admin/admin-slice";
import { ACCOUNT_ADMIN_USERS } from "../../../constants/routeConstants";
import { BaseUserResponse, LoadingStatus, UserResponse } from "../../../types/types";
import { selectUserFromUserState } from "../../../redux-toolkit/user/user-selector";

import "./UsersList.css";

const ROLE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  ROLE_ADMIN:        { label: "Admin",       color: "#6a1b9a", bg: "#f3e5f5" },
  ROLE_USER:         { label: "User",        color: "#1565c0", bg: "#e3f2fd" },
  ROLE_DISTRIBUTOR:  { label: "Distributor", color: "#00695c", bg: "#e0f2f1" },
  ADMIN:             { label: "Admin",       color: "#6a1b9a", bg: "#f3e5f5" },
  USER:              { label: "User",        color: "#1565c0", bg: "#e3f2fd" },
  DISTRIBUTOR:       { label: "Distributor", color: "#00695c", bg: "#e0f2f1" },
};

const getRoleConfig = (role: string) =>
  ROLE_LABELS[role?.toUpperCase()] ?? { label: role ?? "Unknown", color: "#555", bg: "#f5f5f5" };

const getInitial = (name?: string, email?: string): string => {
  if (name && name.trim()) return name.charAt(0).toUpperCase();
  if (email && email.trim()) return email.charAt(0).toUpperCase();
  return "U";
};

const isDistributor = (roles?: Array<string>): boolean =>
  !!roles?.some((r) => r === "ROLE_DISTRIBUTOR" || r === "DISTRIBUTOR");

const PAGE_SIZE = 10; // change to match your backend page size

const UsersList: FC = (): ReactElement => {
  const dispatch      = useDispatch();
  const history       = useHistory();
  const users         = useSelector(selectAdminStateUsers);
  const isLoading     = useSelector(selectIsAdminStateLoading);
  const totalElements = useSelector(selectTotalElements);

  const currentUser = useSelector(selectUserFromUserState) as UserResponse | undefined;
  const isDist      = isDistributor(currentUser?.roles);
  const distId      = localStorage.getItem("distId");

  const [currentPage, setCurrentPage] = useState<number>(1); // Ant Design is 1-based

  useEffect(() => {
    if (!currentUser) return;

    const page = currentPage - 1; // backend is 0-based

    if (isDist) {
      dispatch(fetchAllDistUsers({ distId: String(distId), page }));
    } else {
      dispatch(fetchAllUsers(page));
    }

    return () => {
      dispatch(resetAdminState(LoadingStatus.NEVER));
    };
  }, [dispatch, isDist, currentUser?.id, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUserClick = (userId: number) => {
    history.push(`${ACCOUNT_ADMIN_USERS}/${userId}`);
  };

  return (
    <div className="ul-page">

      {/* HEADER */}
      <div className="ul-header">
        <TeamOutlined className="ul-header-icon" />
        <span className="ul-header-title">
          {isDist ? "My Users" : "All Users"}
        </span>
        {!isLoading && (
          <span className="ul-header-count">
            {totalElements ?? users.length} user{(totalElements ?? users.length) !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* CONTENT */}
      <div className="ul-content">
        {isLoading || !currentUser ? (
          <div className="ul-center">
            <Spin size="large" />
          </div>
        ) : users.length === 0 ? (
          <div className="ul-empty">
            <div className="ul-empty-icon"><UserOutlined /></div>
            <p className="ul-empty-title">No users found</p>
          </div>
        ) : (
          <>
            <div className="ul-list">
              {users.map((user: BaseUserResponse) => {
                const roleCfg = getRoleConfig(user.roles?.[0] ?? "");
                const initial = getInitial(user.firstName, user.email);

                return (
                  <div
                    key={user.id}
                    className="ul-card"
                    onClick={() => handleUserClick(user.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleUserClick(user.id)}
                  >
                    <div className="ul-avatar">{initial}</div>

                    <div className="ul-info">
                      <div className="ul-name">
                        {user.firstName || "—"}
                        <span
                          className="ul-role-badge"
                          style={{ color: roleCfg.color, background: roleCfg.bg }}
                        >
                          {roleCfg.label}
                        </span>
                      </div>
                      <div className="ul-email">{user.email}</div>
                      <div className="ul-meta">
                        <span className="ul-meta-item">
                          <span className="ul-meta-label">ID:</span> #{user.id}
                        </span>
                        {user.provider && (
                          <span className="ul-meta-item">
                            <span className="ul-meta-label">Provider:</span> {user.provider}
                          </span>
                        )}
                      </div>
                    </div>

                    <RightOutlined className="ul-arrow" />
                  </div>
                );
              })}
            </div>

            {/* PAGINATION */}
            {totalElements > PAGE_SIZE && (
              <div className="ul-pagination">
                <Pagination
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  total={totalElements}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showTotal={(total) => `Total ${total} users`}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsersList;